<template>
  <div class="h-full w-full flex flex-col">
    <div class="h-full container mx-auto">
      <div class="px-8 flex flex-col h-full w-full py-8">
        <!-- breadcrumbs start -->
        <div class="w-full bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 flex flex-col">
            <div class="flex items-center text-sm font-light">
                <router-link to="/dashboard" class="opacity-50 hover:opacity-100">Dashboard</router-link>
                <span class="mx-2">/</span>
                <span>{{ getTitle }}</span>
            </div>
            <div class="font-bold">{{ getTitle }}</div>
        </div>
        <!-- breadcrumbs end -->

        <section class="mt-4 h-full w-full flex flex-col bg-white rounded-2xl border shadow-lg p-6 md:p-8">
          <div class="flex justify-between flex-wrap">
              <h1 class="text-base md:text-lg font-semibold mb-4">Projects List</h1>
              <div class="flex justify-start items-center flex-wrap">
                  <div v-if="hasPermission('projects-create')" @click.stop="createModal = true" class="flex items-center bg-ternary hover:bg-white border-ternary border px-4 py-2 rounded-lg cursor-pointer text-white hover:text-ternary mb-4">
                      <icon-plus class="w-4 h-4"></icon-plus>
                      <span class="text-xs ml-2">Add Project</span>
                  </div>
              </div>
          </div>
          <div class="flex flex-col" v-if="$apollo.queries.projects.loading">
              <table-shimmer :titleHeaders="['#', 'Student', 'Name', 'Description', 'Industry', 'Participants', 'Created At', 'Actions',]" :rows="first"></table-shimmer>
          </div>
          <div v-else-if="!$apollo.queries.projects.loading && !hasItems" class="h-full flex justify-center items-center">
              <h1 class="text-center text-xl font-medium">No results found!</h1>
          </div>
          <div v-else class="flex flex-col">
                <div class="flex flex-col overflow-x-auto">
                  <div class="inline-block min-w-full py-2">
                      <div class="overflow-x-auto">
                          <table class="min-w-full text-left text-sm font-light">
                              <thead class="border-b font-medium dark:border-neutral-500">
                                  <tr>
                                      <th scope="col" class="px-6 py-2 text-gray-400 w-8">#</th>
                                      <th scope="col" class="px-6 py-2 text-gray-400">Student</th>
                                      <th scope="col" class="px-6 py-2 text-gray-400">Name</th>
                                      <th scope="col" class="px-6 py-2 text-gray-400">Description</th>
                                      <th scope="col" class="px-6 py-2 text-gray-400">Industry</th>
                                      <th scope="col" class="px-6 py-2 text-gray-400">Participants</th>
                                      <th v-if="role == 'admin' || role == 'superadmin'" scope="col" class="px-6 py-2 text-gray-400 whitespace-nowrap">Created At</th>
                                      <th scope="col" class="px-6 py-2 text-gray-400">Actions</th>
                                  </tr>
                              </thead>
                              <tbody>
                                  <tr v-for="(item, index) in items" :key="index" :class="getBg(item.status)" class="border-b border-dashed dark:border-neutral-500">
                                      <td class="whitespace-nowrap px-6 py-2 font-medium">{{ paginatorInfo.firstItem + index }}</td>
                                      <td class="px-6 py-2">
                                        <div @click.stop="viewStudentProfile(item.user.students.student_id)" class="flex items-center">
                                            <div class="flex-shrink-0 mr-3">
                                                <img
                                                    class="h-12 w-12 rounded-lg object-cover cursor-pointer"
                                                    :src="getProfilePicture(item.user.students.profile_picture_url, item.user.students.student_id, item.user.students.entity)"
                                                    alt=""
                                                >
                                            </div>
                                            <div class="flex-grow flex flex-col">
                                                <h1 class="text-sm font-semibold flex items-center">
                                                    <span
                                                    class="text-heading hover:text-blue-600 hover:underline cursor-pointer"
                                                    >{{ item.user.students.name_en }}</span>
                                                </h1>
                                                <p class="text-xs text-gray-400">{{ item.user.students.name_ar }}</p>
                                            </div>
                                        </div>
                                      </td>
                                      <td class="px-6 py-2">{{ item.name }}</td>
                                      <td class="px-6 py-2 truncate-3" v-html="item.description"></td>
                                      <td class="px-6 py-2">{{ item.industry }}</td>
                                      <td class="px-6 py-2">{{ item.participants }}</td>
                                      <td v-if="role == 'admin' || role == 'superadmin'" class="px-6 py-2">{{ getFormattedDate(item.created_at) }}</td>
                                      <td class="px-6 py-2">
                                        <div class="flex">
                                            <div v-if="hasPermission('projects-show')" @click.stop="viewItem(item)" class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer">
                                                <icon-eye class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-eye>
                                            </div>
                                            <div v-if="hasPermission('projects-edit') && isItemEditable(item.status)" @click.stop="editItem(item)" class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer ml-2">
                                                <icon-pen class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-pen>
                                            </div>
                                            <div v-if="hasPermission('projects-approve') && showApproveRejectIcon(item.status)" @click.stop="approveItem(item.token)" class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer ml-2">
                                                <icon-approve class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-approve>
                                            </div>
                                            <div v-if="hasPermission('projects-approve') && showApproveRejectIcon(item.status)" @click.stop="rejectItem(item.token)" class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer ml-2">
                                                <icon-reject class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-reject>
                                            </div>
                                            <div v-if="hasPermission('projects-delete') && isItemDeletable(item.status)" @click.stop="deleteItem(item.token)" class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer ml-2">
                                                <icon-trash class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-trash>
                                            </div>
                                        </div>
                                      </td>
                                  </tr>
                              </tbody>
                          </table>
                      </div>
                </div>
              </div>
              <div class="mt-4">
                  <pagination
                  :paginatorInfo="paginatorInfo"
                  :setPage="setPage"
                  ></pagination>
              </div>
            </div>

          <transition name="fade" mode="out-in">
              <base-full-modal v-if="createModal" :title="`Create Project`" @close="closeModal">
                  <create class="text-left" @created="onCreated" @close="closeModal"></create>
              </base-full-modal>
          </transition>

          <transition name="fade" mode="out-in">
              <base-full-modal v-if="editModal" :title="`Edit Project - ${selectedItem.name}`" @close="closeModal">
                  <edit class="text-left" :selected-item="selectedItem" @updated="onUpdated" @close="closeModal"></edit>
              </base-full-modal>
          </transition>

          <transition name="fade" mode="out-in">
              <base-full-modal v-if="isViewItem" :title="`View Project - ${selectedItem.name}`" @close="closeModal">
                  <project-view class="text-left" :item="selectedItem" @close="closeModal"></project-view>
              </base-full-modal>
          </transition>

            <transition name="fade" mode="out-in">
                <base-modal v-if="showAddNote" :title="`Add Your Note`" @close="closeAddNote">
                    <div class="flex flex-col">
                        <label for="note" class="px-8 mt-4 text-sm ml-1">Note</label>
                        <div class="h-32 px-8 rounded-xl mt-2">
                            <textarea v-model="note" class="bg-white resize-none w-full h-full outline-none text-sm border p-4 rounded-lg" placeholder="Enter your reason..."></textarea>
                        </div>
                        <div class="flex justify-end mt-4 mr-8">
                            <div @click.stop="addNoteOk" class="border-2 font-medium border-ternary text-white hover:text-ternary bg-ternary hover:bg-white rounded-lg px-5 py-2 uppercase text-sm cursor-pointer">OK</div>
                        </div>
                    </div>
                </base-modal>
            </transition>

            <transition name="fade" mode="out-in">
                <base-full-modal v-if="showStudentProfileFullModal" title="Student Profile" @close="closeStudentProfileModal">
                    <student-profile :student-id="studentId"></student-profile>
                </base-full-modal>
          </transition>

        </section>
      </div>
    </div>
    <div class="h-full flex justify-center items-center" v-if="isLoading">
      <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
    </div>
  </div>
</template>

<script>
import IconPlus from "../../components/icons/IconPlus.vue";
import IconEye from "../../components/icons/IconEye.vue";
import IconPen from "../../components/icons/IconPen.vue";
import IconApprove from "../../components/icons/IconApprove.vue";
import IconReject from "../../components/icons/IconReject.vue";
import IconTrash from "../../components/icons/IconTrash.vue";
import gql from 'graphql-tag';
import Pagination from "../../components/shared/pagination.vue";
import TableShimmer from '../../components/shared/TableShimmer.vue';
import { formattedDate } from "../../helpers/utils";
import Create from "./create.vue";
import Edit from "./edit.vue";
import ProjectView from "./view.vue";
import StudentProfile from "../student-profile/index.vue";
export default {
    name: "my-projects",
    components: {
      IconPlus,
      IconEye,
      IconPen,
      IconApprove,
      IconReject,
      IconTrash,
      Pagination,
      TableShimmer,
      Create,
      Edit,
      ProjectView,
      StudentProfile,
  },
  data() {
      return {
        projects: [],
        first: 10,
        page: 1,
        createModal: false,
        editModal: false,
        isViewItem: false,
        selectedItem: null,
        isLoading: false,
        selectedItemToken: null,
        note: "",
        showAddNote: false,
        showStudentProfileFullModal: false,
        studentId: null,
      };
  },
  watch: {
    projects(newVal) {
        if (newVal.error) {
            if (newVal.error_code == 403) {
                this.$router.replace('/no-permission');
            } else if (newVal.error_code == 401) {
                this.$router.replace('/');
            } else {
                alert(newVal.message);
                this.$router.replace('/');
            }
        }
    },
  },
  computed: {
    getTitle() {
      return this.$route.name;
    },
    items() {
      return this.projects.data;
    },
    hasItems() {
      return this.items && this.items.length > 0;
    },
    paginatorInfo() {
      return this.projects.paginatorInfo;
    },
    role() {
      return this.$store.getters.role;
    },
  },
  apollo: {
    projects() {
      return {
        query: gql`query GetAllProjects($first: Int!, $page: Int!, $is_logged_in: Boolean) {
          projects(first: $first, page: $page, is_logged_in: $is_logged_in) {
            error
            error_code
            message
            data {
              token
              name
              description
              industry
              participants
              status
              note
              approved_by
              approved_at
              created_at
              attachments {
                id
                path
              }
              user {
                students {
                    name_en
                    name_ar
                    profile_picture_url
                    student_id
                    entity
                }
              }
            }
            paginatorInfo {
              count
              currentPage
              firstItem
              hasMorePages
              lastItem
              lastPage
              perPage
              total
            }
          }
        }`,
        variables () {
          return {
            first: this.first,
            page: this.page,
            is_logged_in: true,
          }
        },
        fetchPolicy: 'cache-and-network'
      }
    },
  },
  methods: {
    setPage(page) {
      this.page = page;
    },
    hasPermission(crudName) {
        if (this.role == 'superadmin') {
            return true;
        } else {
            const permissions = this.$store.getters.permissions;
            const permissionsArray = permissions.split(",");
            if (permissionsArray.includes(crudName)) {
                return true;
            } else {
                return false;
            }
        }
    },
    getBg(status) {
      if (status === true) {
          return "bg-green-100";
      } else if (status === false) {
          return "bg-red-100";
      } else {
          return "";
      }
    },
    isItemEditable(status) {
      if (status === true) {
        return false;
      } else {
        return true;
      }
    },
    isItemDeletable(status) {
      if (status === true) {
        return false;
      } else {
        return true;
      }
    },
    getFormattedDate(date) {
      return formattedDate(date);
    },
    showToast(message = '') {
        this.$apollo.queries.projects.refetch();
        const Toast = this.$swal.mixin({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 1000,
            timerProgressBar: true,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', this.$swal.stopTimer)
                toast.addEventListener('mouseleave', this.$swal.resumeTimer)
            }
        })
        Toast.fire({
            icon: 'success',
            title: message
        })
        this.closeModal();
    },
    closeModal() {
        this.createModal = false;
        this.editModal = false;
        this.isViewItem = false;
        this.selectedItem = null;
    },
    onCreated(msg) {
      this.showToast(msg);
    },
    onUpdated(msg) {
      this.showToast(msg);
    },
    viewItem(item) {
        this.selectedItem = item;
        this.isViewItem = true;
    },
    editItem(item) {
        this.selectedItem = item;
        this.editModal = true;
    },
    async deleteItem(token) {
      let error_msg = null;
      let success_msg = null;
      this.$swal.fire({
          title: 'Are you sure?',
          text: "You won't be able to revert this!",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, delete it!'
      }).then(async (result) => {
          if (result.isConfirmed) {

              const DELETE_MUTATION = gql`
                  mutation DeleteProject($token: String!) {
                      deleteProject(token: $token) {
                          error
                          message
                      }
                  }
              `;

              try {
                  await this.$apollo.mutate({
                      mutation: DELETE_MUTATION,
                      variables: {
                          token: token,
                      }
                  }).then((data) => {
                      if (data.data.deleteProject.error) {
                          error_msg = data.data.deleteProject.message;
                      } else {
                          success_msg = data.data.deleteProject.message;
                      }
                  }).catch((error) => {
                      error_msg = error.message;
                  })

              } catch (error) {
                  error_msg = error.message || "Please try again.";
              }

              if (error_msg === null) {
                  this.showToast(success_msg);
              } else {
                  this.$swal.fire({
                      icon: 'error',
                      title: 'Oops...',
                      text: error_msg,
                  }).then((result) => {
                      if (result.value || result.dismiss) {
                          error_msg = null
                      }
                  })
              }
              
          }
      })
    },
    showApproveRejectIcon(status) {
      return status === null;
    },
    approveItem(token) {
      this.approveOrRejectItem(token, true);
    },
    rejectItem(token) {
        this.selectedItemToken = token;
        this.showAddNote = true;
    },
    closeAddNote() {
        this.showAddNote = false;
        this.note = "";
        this.selectedItemToken = null;
    },
    addNoteOk() {
        this.showAddNote = false;
        this.approveOrRejectItem(this.selectedItemToken, false);
    },
    async approveOrRejectItem(token, status) {
            
        this.isLoading = true;
        
        const APPROVE_OR_REJECT_ITEM = gql`
            mutation ApproveOrRejectProject($token: String!, $status: Boolean!, $note: String) {
                approveOrRejectProject(token: $token, status: $status, note: $note) {
                    error
                    message
                }
            }
        `;

        let error_msg = null;
        let success_msg = null;

        try {
            await this.$apollo.mutate({
                mutation: APPROVE_OR_REJECT_ITEM,
                variables: {
                    token: token,
                    status: status,
                    note: this.note,
                }
            }).then((data) => {
                if (data.data.approveOrRejectProject.error) {
                    error_msg = data.data.approveOrRejectProject.message;
                } else {
                    success_msg = data.data.approveOrRejectProject.message;
                }
            }).catch((error) => {
                console.error(error)
            })
        } catch (error) {
            error_msg = error.message || "Failed to authenticate, try later.";
        }

        if (error_msg === null) {
            this.showToast(success_msg);
        } else {
            this.$swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: error_msg,
            }).then((result) => {
                if (result.value || result.dismiss) {
                    error_msg = null
                }
            })
        }

        this.note = "";
        this.isLoading = false;

    },
    getProfilePicture(profilePictureUrl, studentId, entity) {
      if (profilePictureUrl) {
        if (profilePictureUrl == '/storage/alumni-profile-pictures/default-avatar.jpg') {
          return `https://www.iat.ac.ae/sis/Integrations/Get_Student_Image/Q4tphjXBanCMiro8Kpfbi1QEU5qZb01uWtpkQlpfet8/${studentId}/${entity}`;
        } else {
          return process.env.VUE_APP_PUBLIC_URL + profilePictureUrl;
        }
      } else {
        return `https://www.iat.ac.ae/sis/Integrations/Get_Student_Image/Q4tphjXBanCMiro8Kpfbi1QEU5qZb01uWtpkQlpfet8/${studentId}/${entity}`;
      }
    },
    viewStudentProfile(studentId) {
      this.studentId = studentId;
      this.showStudentProfileFullModal = true;
    },
    closeStudentProfileModal() {
      this.showStudentProfileFullModal = false;
      this.studentId = null;
    },
  },
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.truncate-3 {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
}
</style>